import { UseMutationResult } from "react-query";
// @ts-check
import qs from "qs";
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "react-query";
import { useApi } from "../shared/hooks/useApi";
import { MongoDocument } from "../types/MongoDocument";
import { Site } from "../types/Site";
import { CrudDataType } from "../types/CrudDataType";

// Configure the following variables
export const SITE_ENDPOINT = "v1/site";

export const useSearchQuery = (searchParams) => {
  const api = useApi();
  return useQuery<unknown, unknown, CrudDataType<MongoDocument>, Array<string>>(
    [SITE_ENDPOINT, searchParams],
    async () => {
      const response = await api.get(SITE_ENDPOINT, {
        searchParams: qs.stringify({
          limit: searchParams.limit ?? 0,
          skip: searchParams.skip ?? 0,
          sort: searchParams.sort ?? "name",
          ...searchParams,
        }),
      });
      const totalCount = Number(response.headers.get("X-Total-Count"));
      const list = await response.json();

      return {
        totalCount,
        list,
      };
    },
  );
};

export const useFetchItems = () => {
  const api = useApi();
  return async (searchParams) => {
    return api.get(SITE_ENDPOINT, { searchParams: qs.stringify(searchParams) }).json();
  };
};

export const useFindByIdQuery = (itemId: string): UseQueryResult<MongoDocument> => {
  const api = useApi();
  return useQuery([SITE_ENDPOINT, itemId], async () => {
    return api.get(`${SITE_ENDPOINT}/${itemId}`).json();
  });
};

export const useUpsertMutation = (retry: boolean | number = false) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation(
    async (item: Site) => {
      if (item._id) {
        return api.patch(`${SITE_ENDPOINT}/${item._id}`, { json: item }).json();
      }
      delete item._id;
      const site = (await api.post(SITE_ENDPOINT, { json: item }).json()) as Site;
      if (site.footerImage) {
        await api.patch(`${SITE_ENDPOINT}/tags/:id`, { json: site });
      }
      if (site.leftHeaderImage) {
        await api.patch(`${SITE_ENDPOINT}/tags/leftHeader/:id`, { json: site });
      }
      return site;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SITE_ENDPOINT]);
      },
      retry,
    },
  );
};

export const useUpsertPutMutation = (retry: boolean | number = false): UseMutationResult => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation(
    async (item: Site) => {
      if (item._id) {
        return api.put(`${SITE_ENDPOINT}/${item._id}`, { json: item }).json();
      }
      delete item._id;
      return api.post(SITE_ENDPOINT, { json: item }).json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SITE_ENDPOINT]);
      },
      retry,
    },
  );
};

export const useDeleteMutation = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation(
    async (item: Site) => {
      if (item._id) {
        return api.delete(`${SITE_ENDPOINT}/${item._id}`);
      } else if (Array.isArray(item)) {
        return Promise.all(item.map((i) => api.delete(`${SITE_ENDPOINT}/${i._id}`)));
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SITE_ENDPOINT]);
      },
    },
  );
};

export const useUpsertManyMutation = (retry: boolean | number = false) => {
  const { mutateAsync: upsertItem } = useUpsertMutation(retry);
  return useMutation(
    async (items: Site[]) => {
      return Promise.all(items.map((item) => upsertItem(item)));
    },
    {
      retry,
    },
  );
};
